/**
 * THE TWO-RAIL SLOT MACHINE — one wide slot, two rails, and every way a user
 * can ask for it.
 *
 * The provider shell renders a LEFT rail (module navigation + patient identity)
 * and a RIGHT rail (patient actions). They share ONE wide slot: at any moment
 * exactly one of them is 200px with labels and the other is 60px icons.
 *
 * ── A COLLAPSED RAIL IS AN ICON STRIP, NEVER AN EMPTY EDGE (2026-09-23) ──────
 * Until today a chevron on a wide rail HID that rail outright (0px) and handed
 * the slot over. The hand-over was right; the vanishing was not. A 60px strip of
 * icons is still a menu — every destination stays one click away and the user can
 * see what they collapsed — whereas a 0px edge reads as "that rail is gone", which
 * is how a founder review put it: the icons used to show here.
 *
 * So both chevrons are now a two-state toggle: 200 ⇄ 60. The slot still changes
 * hands (the survivor widens, never stranded), but the loser keeps its icons.
 *
 * `leftHidden` is still produced in ONE case: `toggleLeft` with no right rail
 * that can take the slot (see its no-hand-over branch) hides the left rail,
 * because there is nothing to hand the slot to. `rightHidden` is produced by
 * nothing; its 0px geometry stays so that a hidden right rail, if one is ever
 * restored or set by a future caller, can still be escaped. Every chevron
 * transition clears both, so one click heals a stale hidden rail instead of
 * toggling it into a second invisible state.
 *
 * This file is the ONLY place that answers "what happens to the slot when…".
 * Two apps carry the answers out, and neither keeps a rule of its own:
 *  - Merchant.Web: side-bar/rail-slot-shell.tsx translates them into
 *    `App.Instance.showSideBar` / `headerStore.*Hidden` / body classes. All 38
 *    module layouts render through that one shell (layout.comp.tsx). As of
 *    2026-10-19, 18 of them have a right rail that shares the slot (11 PanelRail,
 *    7 legacy RightSideBar), Member's right rail is a fixed 60px strip that
 *    opens drawers and never takes the slot, and 19 have no right rail.
 *  - AgentStudio.Web: stores/NavStore.ts holds a RailState directly.
 *
 * The scenarios this encodes, each with its reasoning:
 *
 *  - DIRECT URL onto a right-rail route → the right rail claims the slot
 *    (`enterRightRailRoute`): the user deep-linked into that rail's feature,
 *    so that rail is the one that must be readable.
 *  - LEAVING right-rail routes → the slot returns to the left
 *    (`leaveRightRailRoute`): the left rail is the module's primary navigation
 *    and the default owner of the slot.
 *  - ANY ROUTE CHANGE → `routeChange`: the slot changes hands only when the rail
 *    that owns the page changes. Browser Back from a right-rail page to a
 *    left-rail page is the case that was missing in Merchant.Web: the left nav
 *    stayed at 60px with the right rail's last item still lit. A move between
 *    two pages of the SAME rail changes nothing, so a chevron choice survives
 *    navigation inside that rail.
 *  - CHEVRON on a rail that is hidden OR at icons → SHOW it wide
 *    (`toggleLeft` / `toggleRight`): a collapse control must always be able to
 *    undo every state that reads as "collapsed", not only the one it created.
 *    That asymmetry — hidden-vs-icons being different states that LOOK the
 *    same — is the bug this module exists to prevent regressing.
 *  - CHEVRON on a wide rail → drop it to 60px ICONS and RELEASE the slot: the
 *    survivor widens, and the rail the user just collapsed still shows what it
 *    holds. It does not disappear (see the 2026-09-23 note above).
 *  - CLICK on a group header or item in a 60px rail → that rail takes the slot:
 *    the click asked to see inside a rail that currently cannot show labels,
 *    and bare unlabeled children are noise, not an answer.
 *
 * Pure by design: state in, state out, no App.Instance, no DOM — so Vitest can
 * hold the whole scenario matrix without a browser.
 */

/** Who currently owns the single wide (200px) slot. */
export type WideSlotOwner = 'left' | 'right';

export interface RailState {
  /** Owner of the wide slot; the other rail sits at 60px icons. */
  wideSlot: WideSlotOwner;
  /** The left rail is fully hidden (0px). */
  leftHidden: boolean;
  /** The right rail is fully hidden (0px). */
  rightHidden: boolean;
  /**
   * The wide slot is on LOAN to the left rail. Set when the route belongs to the
   * RIGHT rail and the user widened the left just to look at its menus — a peek,
   * not a decision. A click in the page content returns the slot to the right
   * rail so the wide rail keeps reflecting where the user IS; navigating to a
   * left-rail route converts the loan into ownership instead. Explicit chevron
   * clicks always settle the loan: they are decisions. AgentStudio.Web only; see
   * `peekLeft` for why Merchant.Web never lends the slot.
   */
  leftPeek?: boolean;
}

/**
 * The shell's boot state: left rail wide, right rail icons, nothing hidden.
 * Merchant.Web re-enters it on every module change, so a rail hidden in one
 * module does not arrive hidden in the next (rail-slot-shell.tsx `bootRails`).
 */
export const initialRailState: RailState = {
  wideSlot: 'left',
  leftHidden: false,
  rightHidden: false,
};

export interface RailWidths {
  left: 0 | 60 | 200;
  right: 0 | 60 | 200;
  /** #page-wrapper margins — the content must butt against whatever is beside it. */
  contentMarginLeft: 0 | 60 | 200;
  contentMarginRight: 0 | 60 | 200;
}

/**
 * The geometry a state renders to. Single source for the shell's widths AND
 * the margins, so a state can never produce a gutter (margin without a rail)
 * or an overlap (rail without a margin).
 */
export const railWidths = (s: RailState): RailWidths => {
  const left: 0 | 60 | 200 = s.leftHidden ? 0 : s.wideSlot === 'left' ? 200 : 60;
  const right: 0 | 60 | 200 = s.rightHidden ? 0 : s.wideSlot === 'right' ? 200 : 60;
  return { left, right, contentMarginLeft: left, contentMarginRight: right };
};

/** Is this rail in ANY state a user reads as "collapsed"? (hidden or icons) */
export const isLeftCollapsed = (s: RailState): boolean => s.leftHidden || s.wideSlot !== 'left';
export const isRightCollapsed = (s: RailState): boolean => s.rightHidden || s.wideSlot !== 'right';

/**
 * The left chevron — a two-state toggle on the WIDE SLOT, not a visibility switch.
 * Collapsed (hidden OR icons) → take the slot and show 200px of labels. Wide →
 * hand the slot to the right rail and sit at 60px icons, which is a collapsed
 * menu the user can still read and click, not an empty edge.
 * Both hidden flags clear: a chevron click is a decision, and it settles every
 * state that reads as "collapsed", including a stale hidden rail. Any
 * outstanding peek settles here too.
 *
 * <param>rightRailTakesSlot</param> — false when no right rail can take the
 * slot: the module renders none, or its right rail is a fixed 60px strip that
 * opens drawers (Merchant.Web's Member). See the branch below.
 */
export const toggleLeft = (s: RailState, rightRailTakesSlot: boolean = true): RailState =>
  isLeftCollapsed(s)
    ? { wideSlot: 'left', leftHidden: false, rightHidden: false }
    : rightRailTakesSlot
      // Hand the slot over and stay on as icons.
      ? { wideSlot: 'right', leftHidden: false, rightHidden: false }
      // ⚠ NOTHING CAN TAKE THE SLOT (Merchant.Web: the 19 modules with no right rail,
      // and Member, whose right rail is a fixed strip). Handing the slot to a rail
      // that cannot widen leaves no wide rail at all, and in Member it opened that
      // rail's drawer with nothing in it, because the drawer read the slot flag as
      // "open" (fixed 2026-10-19). A 60px strip beside a 200px content margin would
      // be a gutter, because those shells take their left margin from CSS
      // (#page-wrapper{margin-left:200px}) rather than from railWidths(). So the only
      // collapse they can render is the one they always rendered: hidden, via
      // body.left-sidebar-hidden. The 2026-09-23 rule is about the HAND-OVER and has
      // no meaning when there is no rail to hand over to.
      : { wideSlot: 'left', leftHidden: true, rightHidden: false };

/** The right chevron — the exact mirror. */
export const toggleRight = (s: RailState): RailState =>
  ({ wideSlot: isRightCollapsed(s) ? 'right' : 'left', leftHidden: false, rightHidden: false });

/**
 * A click INSIDE a 60px rail (group header or item): the rail takes the slot
 * and un-hides itself — labels are the answer the click was asking for. A click
 * inside an already-wide rail changes nothing here (navigation is the caller's
 * business).
 */
export const claimLeft = (s: RailState): RailState =>
  ({ wideSlot: 'left', leftHidden: false, rightHidden: s.rightHidden });

export const claimRight = (s: RailState): RailState =>
  ({ wideSlot: 'right', leftHidden: s.leftHidden, rightHidden: false });

/** Direct URL / route change onto a route the right rail owns. */
export const enterRightRailRoute = claimRight;

/** Leaving the right rail's routes: the slot returns to its default owner. */
export const leaveRightRailRoute = (s: RailState): RailState =>
  ({ wideSlot: 'left', leftHidden: s.leftHidden, rightHidden: s.rightHidden });

/**
 * The route moved from a page owned by `from` to a page owned by `to`.
 *
 * The slot changes hands only when the OWNING rail changes: onto a right-rail
 * page the right rail claims it (`enterRightRailRoute`); off one, it returns to
 * the left (`leaveRightRailRoute`). A move between two pages of the same rail is
 * a no-op, so a chevron choice survives navigation inside that rail, and a
 * click in a rail that already claimed the slot is not undone by the navigation
 * it caused.
 *
 * <param>from</param> — null for the first page the shell sees. Deciding the
 * first page is the deep-link case: onto a right-rail page the right rail
 * claims the slot; onto a left-rail page nothing changes (the boot state
 * already has the left rail wide).
 */
export const routeChange = (s: RailState, from: WideSlotOwner | null, to: WideSlotOwner): RailState =>
  to === from ? s
    : to === 'right' ? enterRightRailRoute(s)
      : from === 'right' ? leaveRightRailRoute(s)
        : s;

/**
 * A group click on the LEFT rail while the ROUTE belongs to the right rail:
 * the left rail takes the slot to show its labels, but only as a LOAN — the
 * user has not left the right rail's screen, so the widening must not read as
 * a change of place. From any other state it is a plain claim.
 *
 * AgentStudio.Web peeks (NavStore.claimRail). Merchant.Web does NOT, and its
 * wiring was deleted on 2026-10-19 after it was found dead: nothing ever set the
 * loan. It cannot happen there: a slim group header is pointer-events:none
 * (rail-chrome.ts), and every other click in its 60px left rail navigates, so
 * `routeChange` settles the slot at once.
 */
export const peekLeft = (s: RailState): RailState =>
  s.wideSlot === 'right'
    ? { ...claimLeft(s), leftPeek: true }
    : claimLeft(s);

/**
 * A click in the PAGE CONTENT while a peek is outstanding.
 *
 * <param>routeChanged</param> — whether the user navigated since the peek began.
 * If they did NOT (they looked at the menus and went back to working on the
 * right rail's screen), the slot returns to the right rail so the wide rail
 * matches where they are. If they DID navigate, the loan became ownership: the
 * left rail keeps the slot and only the marker clears. Without a peek this is
 * a no-op — content clicks must never fight a deliberate layout choice.
 */
export const contentClick = (s: RailState, routeChanged: boolean): RailState => {
  if (!s.leftPeek) return s;
  if (routeChanged) return { wideSlot: s.wideSlot, leftHidden: s.leftHidden, rightHidden: s.rightHidden };
  return { wideSlot: 'right', leftHidden: s.leftHidden, rightHidden: s.rightHidden };
};
